/**
 * V2 Survey Section types for QuestionsWithSections editor.
 *
 * `SurveyQuestionBase` extends `IFormField<string>` from the V1 AdditionalQuestions system so
 * that all existing question fields (label, fieldType, options, isOptional, etc.) are carried
 * forward. V2 adds the section-and-discriminator layer (`_type`, `fieldId`, `visibleWhen`) on
 * top of the V1 shape rather than redefining those fields.
 *
 * The discriminator field `_type` mirrors the BE `[JsonPolymorphic(TypeDiscriminatorPropertyName = "_type")]`
 * attribute on `SurveyPart` in Levitate.Api.Models/Surveys/Survey.cs.
 */
import { IFormField } from '@ViewModels';

// ---------------------------------------------------------------------------
// Question discriminator
// ---------------------------------------------------------------------------

export type QuestionFieldType =
	| 'ShortAnswerQuestion'
	| 'DropDownQuestion'
	| 'CheckBoxesQuestion'
	| 'FileUploadQuestion';

// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------

export interface QuestionVisibilitySimple {
	_type: 'SimpleEqualityQuestionVisibility';
	fieldId: string;
	fieldValue: string;
}

export interface QuestionVisibilityLogical {
	_type: 'LogicalQuestionVisibility';
	visibilities: QuestionVisibility[];
	logicalOperator: 'And' | 'Or';
}

export type QuestionVisibility = QuestionVisibilitySimple | QuestionVisibilityLogical;

// ---------------------------------------------------------------------------
// Survey questions — extend V1 IFormField<string> to carry forward all
// existing question fields (label, fieldType, options, isOptional, etc.)
// ---------------------------------------------------------------------------

/**
 * Omit `_type` from `IFormField` so we can replace it with the V2 discriminator
 * (`QuestionFieldType`). `IFormField._type` is typed as `ApiModelType | undefined`,
 * which does not include V2 discriminator values — extending directly would fail tsc.
 * All other V1 fields (label, fieldType, options, isOptional, etc.) are carried forward.
 */
interface SurveyQuestionBase extends Omit<IFormField<string>, '_type'> {
	/** Required id — narrows IBaseApiModel's optional id to required. */
	id: string;
	/** Stable field identifier used by conditional visibility rules. */
	fieldId: string;
	/** V2 discriminator matching BE `_type` on SurveyPart. */
	_type: QuestionFieldType;
	/** Optional conditional visibility rule for this question. */
	visibleWhen?: QuestionVisibility;
}

export interface ShortAnswerQuestion extends SurveyQuestionBase {
	_type: 'ShortAnswerQuestion';
}

export interface DropDownQuestion extends SurveyQuestionBase {
	_type: 'DropDownQuestion';
}

export interface CheckBoxesQuestion extends SurveyQuestionBase {
	_type: 'CheckBoxesQuestion';
}

export interface FileUploadQuestion extends SurveyQuestionBase {
	_type: 'FileUploadQuestion';
}

export type SurveyQuestion = ShortAnswerQuestion | DropDownQuestion | CheckBoxesQuestion | FileUploadQuestion;

// ---------------------------------------------------------------------------
// Survey section
// ---------------------------------------------------------------------------

export interface SurveySection {
	_type: 'SurveySection';
	id: string;
	title?: string;
	description?: string;
	questions: SurveyQuestion[];
	visibleWhen?: QuestionVisibility;
}

// ---------------------------------------------------------------------------
// Top-level union and type guards
// ---------------------------------------------------------------------------

export type SurveyPart = SurveySection | SurveyQuestion;

export const isSurveySection = (part: SurveyPart): part is SurveySection => part._type === 'SurveySection';
